import { useEffect, useRef } from 'react'

import { productCodeLittleLoans } from '@ilg-totallymoney/cardloanutils'
import { useRouter } from 'next/router'

import Box from '@ilg-totallymoney/ui/components/Box'
import Grid from '@ilg-totallymoney/ui/components/Grid'
import Heading from '@ilg-totallymoney/ui/components/Heading'
import LinkWithChevron from '@ilg-totallymoney/ui/components/LinkWithChevron'
import Stack from '@ilg-totallymoney/ui/components/Stack'
import Text from '@ilg-totallymoney/ui/components/Text'
import theme from '@ilg-totallymoney/ui/theme'

import {
    CreditCardAlternativeProduct,
    CreditCardAlternativeProductsCategory,
} from '@appTypes/EligibilityApi.interface'

import BrokerInfo from '@components/BrokerInfo'
import EligibilityFactorsCard from '@components/EligibilityFactorsCard'

import useAppQueryState from '@hooks/useAppQueryState'
import useFeatureFlag from '@hooks/useFeatureFlag'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'
import useLogEvents from '@hooks/useLogEvents'
import useLogEvents2 from '@hooks/useLogEvents2'
import useSetState from '@hooks/useSetState'

import { useHestiaPredictionsQuery } from '@queries/useHestiaPredictionsQuery'
import { useMinimumGuaranteedCreditLimitQuery } from '@queries/useMinimumGuaranteedCreditLimitQuery'

import useGetAlternativeProductApplicationUrl from '@routes/AlternativeProductsTableRoute/hooks/useGetAlternativeProductApplicationUrl'
import HighlightHeroIneligible from '@routes/ScoredTableRoute/components/ScoredTable/components/HighlightHero/components/HighlightHeroIneligible'

import {
    buildAcquisitionJourneyAlternativeProductsTableClickoutEventPayload,
    buildAcquisitionJourneyFormDeclineEventPayload,
} from '@utils/buildAcquisitionEvents'
import { getAppConfig } from '@utils/getAppConfig'
import getCardPosition from '@utils/getCardPosition'
import { getClickId } from '@utils/getClickId'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'

import AlternativeProductsTableHeader from './components/AlternativeProductsTableHeader'
import AlternativeProductsTableItem from './components/AlternativeProductsTableItem'
import LittleLoansOfferCard from './components/LittleLoansOfferCard'
import getCategoryByAlternativeCategoryId from './utils/getCategoryByAlternativeProductCategoryId'
import getEligibleAltProductCategories from './utils/getEligibleAltProductCategories'
import getIsHighHomeownerLoanLikelihood from './utils/getIsHighHomeownerLoanLikelihood'

interface AlternativeProductsTableProps {
    alternativeProductCategories: CreditCardAlternativeProductsCategory[]
    numberOfRedEligibilityFactors?: number
}

interface AlternativeProductsTableState {
    selectedCategory: string
}

const AlternativeProductsTable = ({
    alternativeProductCategories,
    numberOfRedEligibilityFactors = 0,
}: AlternativeProductsTableProps) => {
    const appConfig = getAppConfig()
    const { appQueryState } = useAppQueryState()
    const { query, isReady } = useRouter()
    const { trackDecline, trackPageViewAlt, trackAction, trackClickoutAlt } =
        useInscalarTrackEvent()
    const experiment_appPromotionImproveEligibilityCreditCardsAlt =
        useFeatureFlag(
            'experiment_appPromotionImproveEligibilityCreditCardsAlt'
        )

    const getAlternativeProductApplicationUrl =
        useGetAlternativeProductApplicationUrl()
    const logEvents = useLogEvents()
    const { logAlternativeProductClickEvent, logResultsScreenDisplayedEvent } =
        useLogEvents2()

    const { data } = useMinimumGuaranteedCreditLimitQuery({
        customerId: appQueryState.customerId,
    })

    const { data: hestiaPredictions } = useHestiaPredictionsQuery({
        customerId: appQueryState.customerId,
    })
    const eligibleAltProductCategories = getEligibleAltProductCategories(
        alternativeProductCategories,
        hestiaPredictions
    )
    const isNewSignUpJourney = query.isNewSignUp === 'true'

    const [alternativeProductsState, setAlternativeProductsState] =
        useSetState<AlternativeProductsTableState>({
            selectedCategory: eligibleAltProductCategories[0]?.id ?? '',
        })

    const activeCategory = getCategoryByAlternativeCategoryId(
        eligibleAltProductCategories,
        alternativeProductsState.selectedCategory
    )

    useEffect(() => {
        if (!activeCategory && eligibleAltProductCategories.length > 0) {
            setAlternativeProductsState({
                selectedCategory: eligibleAltProductCategories[0].id,
            })
        }
    }, [eligibleAltProductCategories])

    const inscalarJourney = getIsHighHomeownerLoanLikelihood(hestiaPredictions)
        ? 'homeowner_loans'
        : 'personal_loans'

    useEffect(() => {
        if (activeCategory && activeCategory.alternativeProducts.length > 0) {
            const eventData = buildAcquisitionJourneyFormDeclineEventPayload(
                activeCategory.alternativeProducts,
                isNewSignUpJourney
            )

            if (eventData) {
                logEvents.logAcquisitionJourneyCardsTableViewEvent(eventData)
            }
        }
    }, [isReady])

    const onCategoryChange = (category: string) => {
        logEvents.logAlternativeProductCategoryChangeEvent({
            categoryName: category,
        })

        setAlternativeProductsState({
            selectedCategory: category,
        })

        trackAction({
            step: 'credit_cards_alternative_products_table',
            journey: inscalarJourney,
            journeyCategory: 'loans',
        })
    }

    const hasFiredInitialAltMountRef = useRef(false)

    useEffect(() => {
        if (!isReady || hestiaPredictions === undefined) {
            return
        }

        const littleLoansProduct = {
            apr: 79.5,
            name: 'Little Loans',
            productCode: productCodeLittleLoans,
        } as CreditCardAlternativeProduct

        const products = [
            ...(activeCategory?.alternativeProducts ?? []),
            littleLoansProduct,
        ]

        if (!hasFiredInitialAltMountRef.current) {
            hasFiredInitialAltMountRef.current = true

            trackDecline({
                step: 'credit_cards_scoring',
                journey: 'credit_cards',
                journeyCategory: 'credit_cards',
            })
            trackDecline({
                step: 'personal_loans_scoring',
                journey: 'personal_loans',
                journeyCategory: 'loans',
            })

            trackAction({
                step: 'homeowner_loan_likelihood_check',
                journey: inscalarJourney,
                journeyCategory: 'loans',
            })
        }

        trackPageViewAlt({
            step: 'credit_cards_alternative_products_table',
            journey: inscalarJourney,
            journeyCategory: 'loans',
            products,
        })

        logResultsScreenDisplayedEvent({
            displayedProducts: products,
            highlightProduct: null,
            category: 'credit_cards_alt',
            maxCreditLimit: data?.minimumGuaranteedCreditLimit!,
        })
    }, [
        alternativeProductsState.selectedCategory,
        isReady,
        hestiaPredictions !== undefined,
    ])

    const onApplyPress = ({
        alternativeProduct,
        productPosition,
        applicationUrl,
        clickId,
        index,
    }: {
        alternativeProduct: CreditCardAlternativeProduct
        applicationUrl: string
        clickId: string
        index: number
        productPosition: number
    }) => {
        logEvents.logAlternativeProductApplyEvent({
            productCode: alternativeProduct.productCode,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            rowNumber: productPosition,
            trigger: 'row',
        })

        trackClickoutAlt({
            step: 'credit_cards_alternative_products_table',
            product: alternativeProduct,
            journey: inscalarJourney,
            journeyCategory: 'loans',
            productPosition: index,
        })

        logAlternativeProductClickEvent({
            altCreditCard: alternativeProduct,
            category: 'credit_cards_alt',
            productShownOrder: productPosition,
            productRankOrder: null,
            clickId,
        })

        window.open(applicationUrl, '_blank')
    }

    const showHighlightedCreditCardHero =
        appQueryState.scoredCardUrl != null && appQueryState.isCardSpecific

    const showEligibilityFactorsCard =
        experiment_appPromotionImproveEligibilityCreditCardsAlt === 'v1' &&
        numberOfRedEligibilityFactors >= 1

    return (
        <>
            <Box backgroundColor="productNeutral80">
                <Grid>
                    <Box mt="space30" mb="space10" gridColumn="1/13">
                        <LinkWithChevron
                            href={`${appConfig.ACCOUNT_URL}offers?customerId=${appQueryState.customerId}`}
                            variant="100"
                            direction="left"
                        >
                            Back to offers
                        </LinkWithChevron>
                    </Box>
                </Grid>
                <Grid>
                    <Box gridColumn="1/13" pb="space20">
                        <Stack space="space30">
                            <Stack space="space10">
                                <Heading variant="100">
                                    Your card results
                                </Heading>
                                <Text variant="120">
                                    We couldn’t find any cards you’re eligible
                                    for right now, but we’ve got some other
                                    options for you.
                                </Text>
                            </Stack>

                            {showHighlightedCreditCardHero ? (
                                <HighlightHeroIneligible
                                    item={null}
                                    totalCreditCards={0}
                                />
                            ) : null}
                        </Stack>
                    </Box>
                </Grid>
            </Box>
            <Box backgroundColor="productNeutral80">
                {eligibleAltProductCategories.length > 0 ? (
                    <Box pt="space20">
                        <AlternativeProductsTableHeader
                            categories={eligibleAltProductCategories}
                            initialCategory={eligibleAltProductCategories[0].id}
                            onCategoryChange={onCategoryChange}
                        />

                        <Box pb="space50" />
                        <Grid>
                            <Box gridColumn="1/13" mb="space50">
                                <Box mb="space20">
                                    <Heading variant="60">
                                        {activeCategory?.displayName}
                                    </Heading>
                                </Box>
                                <Text variant="120">
                                    {activeCategory?.description}
                                </Text>
                            </Box>
                        </Grid>
                        <Grid style={{ gridGap: theme.space30 }}>
                            {activeCategory?.alternativeProducts.map(
                                (alternativeProduct, index) => {
                                    const clickId = getClickId()
                                    const applyUrl =
                                        getAlternativeProductApplicationUrl({
                                            productCode:
                                                alternativeProduct.productCode,
                                            clickId,
                                        })

                                    return (
                                        <Box
                                            gridColumn="1/13"
                                            key={alternativeProduct.productCode}
                                        >
                                            <AlternativeProductsTableItem
                                                item={alternativeProduct}
                                                applyHref={applyUrl}
                                                isPortraitImage={getIsProductPortraitImage(
                                                    alternativeProduct.productCode
                                                )}
                                                onApplyPress={() => {
                                                    const acquisitionClickoutEventData =
                                                        buildAcquisitionJourneyAlternativeProductsTableClickoutEventPayload(
                                                            alternativeProduct,
                                                            activeCategory.alternativeProducts,
                                                            isNewSignUpJourney
                                                        )

                                                    if (
                                                        acquisitionClickoutEventData
                                                    ) {
                                                        logEvents.logAcquisitionJourneyCardsTableClickoutEvent(
                                                            acquisitionClickoutEventData
                                                        )
                                                    }

                                                    onApplyPress({
                                                        alternativeProduct,
                                                        applicationUrl:
                                                            applyUrl,
                                                        index,
                                                        productPosition:
                                                            getCardPosition(
                                                                index
                                                            ),
                                                        clickId,
                                                    })
                                                }}
                                            />
                                        </Box>
                                    )
                                }
                            )}
                        </Grid>
                    </Box>
                ) : null}

                <Grid>
                    <Box gridColumn="1/13" mt="space40" mb="space30">
                        <Stack space="space10">
                            <Heading variant="60">
                                Loans alternative products
                            </Heading>
                            <Text variant="120">
                                Check out our partner Little Loans and get a
                                personalised loan offer today.
                            </Text>
                        </Stack>
                    </Box>
                </Grid>

                <Grid>
                    <Box gridColumn="1/13" mb="space30">
                        <LittleLoansOfferCard
                            inscalarJourney={inscalarJourney}
                            productPosition={
                                activeCategory?.alternativeProducts.length
                            }
                        />
                    </Box>
                </Grid>

                {showEligibilityFactorsCard ? (
                    <Grid>
                        <Box gridColumn="1/13" mb="space30">
                            <EligibilityFactorsCard
                                route="alternativeProducts"
                                numberOfRedEligibilityFactors={
                                    numberOfRedEligibilityFactors
                                }
                            />
                        </Box>
                    </Grid>
                ) : null}

                <Grid>
                    <Box gridColumn="1/13" mb="space30">
                        <BrokerInfo />
                    </Box>
                </Grid>
            </Box>
        </>
    )
}

export default AlternativeProductsTable
